import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { PaymentBanner } from 'dpl/common/components/PriceBreakdownModal';
import {
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';

import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';
import {
  getDepositRefundPolicyAdjective,
  getDepositSecuresText,
  getFinalPaymentTimingText
} from './util';

const MODAL_TITLE = 'Deposit terms';

function getDepositTermsSentence({
  depositRefundPolicy,
  depositSecures,
  finalPaymentTiming
}) {
  const refundPolicyAdjective =
    getDepositRefundPolicyAdjective(depositRefundPolicy);
  // The deposit terms modal never names a dog, even for a specific_puppy
  // deposit, so getDepositSecuresText is called without a dog name.
  const securesText = getDepositSecuresText(depositSecures);
  const timingText = getFinalPaymentTimingText(finalPaymentTiming);

  const sentences = [];

  if (securesText) {
    sentences.push(
      `This ${
        refundPolicyAdjective ? `${refundPolicyAdjective} ` : ''
      }deposit reserves ${securesText}.`
    );
  } else if (refundPolicyAdjective) {
    // No secures clause to attach the adjective to, so state it as its own
    // claim rather than the dangling "This refundable deposit."
    sentences.push(`This deposit is ${refundPolicyAdjective}.`);
  }

  if (timingText) {
    sentences.push(`The remaining balance is due ${timingText}.`);
  }

  return sentences.join(' ');
}

/** @param {any} props */
function DepositTermsContent({
  depositRefundPolicy = null,
  depositSecures = null,
  finalPaymentTiming = null,
  tooltipDescription = null
}) {
  const sentence = getDepositTermsSentence({
    depositRefundPolicy,
    depositSecures,
    finalPaymentTiming
  });

  return (
    <>
      {sentence && <p>{sentence}</p>}
      {tooltipDescription && (
        <p
          className={sentence ? 'mt4' : null}
        >{`Breeder’s policy: “${tooltipDescription}”`}</p>
      )}
      <PaymentBanner
        className="mt6"
        view={ANALYTICS_VIEWS.PAYMENT_DETAILS}
        viewFeatureLocation={
          ANALYTICS_VIEW_FEATURE_LOCATIONS.PAYMENT_CHECKOUT_PAGE
        }
      />
    </>
  );
}

function MobileTooltipDisplay(props) {
  return (
    <div className="MobileTooltipDisplay">
      <div className="mb6 bb b--lightest-gray">
        <h5 className="font-20 pb3 tc pt1">{MODAL_TITLE}</h5>
      </div>
      <div className="font-16 ph6-md">
        <DepositTermsContent {...props} />
      </div>
    </div>
  );
}

function DesktopModalDisplay(props) {
  return (
    <div className="DesktopModalDisplay pv5">
      <div className="container">
        <div className="row pb3 ph4 mb6">
          <h5 className="font-24 col">{MODAL_TITLE}</h5>
        </div>
        <div className="row pb6 ph8">
          <div className="font-16">
            <DepositTermsContent {...props} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** @param {Record<string, any>} props */
export default function StripeChargeModalDepositLineItem({
  amountInDollars,
  discountedPriceInDollars = null,
  paymentMethod = null,
  tooltipDescription,
  depositRefundPolicy,
  depositSecures,
  finalPaymentTiming,
  ...rest
}) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const showDiscount =
    paymentMethod && discountedPriceInDollars !== amountInDollars;

  const depositTermsProps = {
    depositRefundPolicy,
    depositSecures,
    finalPaymentTiming,
    tooltipDescription
  };

  return (
    <StripeChargeModalPaymentSummaryLineItemV2
      iconProps={{ name: 'fetch-info' }}
      amountInDollars={amountInDollars}
      {...rest}
      discountedPriceInDollars={showDiscount ? discountedPriceInDollars : null}
      tooltipProps={{
        minWidth: '250px',
        title: <MobileTooltipDisplay {...depositTermsProps} />,
        isVisible: isTooltipOpen,
        onClick: () => setIsTooltipOpen(true),
        onCloseTooltipClick: () => setIsTooltipOpen(false)
      }}
      modalProps={{
        content: <DesktopModalDisplay {...depositTermsProps} />,
        maxWidth: '600px',
        closeButtonClassName: 'pv2 ph2 mt4 mr4 ba b--light-gray bg-white br-100'
      }}
    />
  );
}

const depositTermsPropTypes = {
  depositRefundPolicy: PropTypes.string,
  depositSecures: PropTypes.string,
  finalPaymentTiming: PropTypes.string,
  tooltipDescription: PropTypes.string
};

DepositTermsContent.propTypes = depositTermsPropTypes;
MobileTooltipDisplay.propTypes = depositTermsPropTypes;
DesktopModalDisplay.propTypes = depositTermsPropTypes;

StripeChargeModalDepositLineItem.propTypes = {
  amountInDollars: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  paymentMethod: PropTypes.string,
  longDescription: PropTypes.string,
  discountedPriceInDollars: PropTypes.string,
  ...depositTermsPropTypes
};
